import { httpClient } from '@/lib/httpClient';
import { API_PROXY_PREFIX } from '@/lib/data-source/base-url';
import type {
  PublicCheckoutData,
  PublicCheckoutResponse,
  PublicCheckoutFieldKey,
  PublicCheckoutFieldSetting,
  PublicCheckoutCountryOption,
  PublicCheckoutCountryOptions,
} from '@/types/public-checkout';

type CheckoutRequestBody = {
  checkout_token?: string;
  product_id: string;
  plan_id?: string;
  package_id?: string;
  cycle_id?: string;
  price_id?: string;
  currency?: string;
  form: {
    name: string;
    email: string;
    phone?: string;
    company?: string;
    billing_address_line_1?: string;
    billing_address_line_2?: string;
    billing_address_city?: string;
    billing_address_state?: string;
    billing_address_postal_code?: string;
    billing_address_country?: string;
    language?: string;
    role?: 'individual' | 'team_owner';
    team_name?: string;
  };
  utm?: Record<string, string | undefined>;
};

type ApiEnvelope<T> = {
  data?: T;
  message?: string;
  errors?: Record<string, string[]>;
};

const readString = (value: unknown) => (typeof value === 'string' ? value : undefined);

const normalizeCountryOption = (value: unknown): PublicCheckoutCountryOption | null => {
  if (!value || typeof value !== 'object') {
    return null;
  }

  const record = value as Record<string, unknown>;
  const code = readString(record.code)?.trim().toUpperCase();
  const name = readString(record.name)?.trim();
  if (!code || !name || !/^[A-Z]{2}$/.test(code)) {
    return null;
  }

  return { code, name };
};

const normalizeCountryOptionList = (value: unknown): PublicCheckoutCountryOption[] => {
  const entries = Array.isArray(value) ? value : [];
  const seen = new Set<string>();
  const normalized: PublicCheckoutCountryOption[] = [];

  entries.forEach((entry) => {
    const option = normalizeCountryOption(entry);
    if (!option || seen.has(option.code)) {
      return;
    }
    seen.add(option.code);
    normalized.push(option);
  });

  return normalized;
};

const normalizeCountryOptions = (value: unknown): PublicCheckoutCountryOptions | null => {
  if (!value || typeof value !== 'object') {
    return null;
  }

  const record = value as Record<string, unknown>;
  return {
    priority: normalizeCountryOptionList(record.priority),
    remaining: normalizeCountryOptionList(record.remaining),
  };
};

export class PublicCheckoutRequestError extends Error {
  status?: number;
  payload?: unknown;
  errors?: Record<string, string[]>;
  code?: string;

  constructor(message: string, status?: number, payload?: unknown) {
    super(message);
    this.status = status;
    this.payload = payload;
    if (payload && typeof payload === 'object' && 'errors' in (payload as Record<string, unknown>)) {
      this.errors = (payload as { errors?: Record<string, string[]> }).errors;
    }
    if (payload && typeof payload === 'object' && 'code' in (payload as Record<string, unknown>)) {
      const codeValue = (payload as { code?: unknown }).code;
      if (typeof codeValue === 'string') {
        this.code = codeValue;
      }
    }
  }
}

function normalizeCheckoutData(payload: ApiEnvelope<PublicCheckoutData> | undefined): PublicCheckoutData {
  if (!payload || typeof payload !== 'object') {
    throw new PublicCheckoutRequestError('Invalid checkout response.');
  }

  const data = payload.data;
  if (!data || typeof data !== 'object' || Array.isArray(data)) {
    throw new PublicCheckoutRequestError('Checkout response is not JSON. Check API/proxy configuration.');
  }

  const readArray = (value: unknown) => (Array.isArray(value) ? value : []);
  const dataRecord = data as unknown as Record<string, unknown>;
  const rawCreditPackages =
    'creditPackages' in data
      ? (data as PublicCheckoutData).creditPackages
      : 'credit_packages' in dataRecord
        ? dataRecord.credit_packages
        : [];

  const creditPackages = readArray(rawCreditPackages) as PublicCheckoutData['creditPackages'];
  const rawCheckoutFields =
    'checkoutFields' in data
      ? (data as PublicCheckoutData).checkoutFields
      : 'checkout_fields' in dataRecord
        ? dataRecord.checkout_fields
        : [];
  const checkoutFields = normalizeCheckoutFields(rawCheckoutFields);
  const rawCountryOptions =
    'countryOptions' in data
      ? (data as PublicCheckoutData).countryOptions
      : 'country_options' in dataRecord
        ? dataRecord.country_options
        : null;
  const countryOptions = normalizeCountryOptions(rawCountryOptions);

  const rawPlans =
    'plans' in data && Array.isArray((data as PublicCheckoutData).plans)
      ? (data as PublicCheckoutData).plans
      : [];
  const hasVariantHints = rawPlans.some((plan) => {
    if (!plan || typeof plan !== 'object') return false;
    const record = plan as unknown as Record<string, unknown>;
    return (
      'parentPlanId' in record ||
      'parent_plan_id' in record ||
      'isVariant' in record ||
      'is_variant' in record
    );
  });
  const plans = hasVariantHints
    ? rawPlans.filter((plan) => {
        if (!plan || typeof plan !== 'object') return false;
        const record = plan as unknown as Record<string, unknown>;
        const parentPlanId =
          (record.parentPlanId as string | null | undefined) ??
          (record.parent_plan_id as string | null | undefined);
        const isVariant =
          (record.isVariant as boolean | undefined) ??
          (record.is_variant as boolean | undefined);
        return !parentPlanId && !isVariant;
      })
    : rawPlans;

  const rawPrefill =
    'prefill' in data
      ? (data as PublicCheckoutData).prefill
      : (dataRecord.prefill as Record<string, unknown> | undefined);
  const prefillRecord = rawPrefill && typeof rawPrefill === 'object' ? rawPrefill : undefined;

  return {
    productId: 'productId' in data && typeof data.productId === 'string' ? data.productId : '',
    defaultCurrency: 'defaultCurrency' in data && typeof data.defaultCurrency === 'string' ? data.defaultCurrency : undefined,
    defaultPlanId: 'defaultPlanId' in data && typeof data.defaultPlanId === 'string' ? data.defaultPlanId : undefined,
    recommendedPlanId:
      'recommendedPlanId' in data && typeof data.recommendedPlanId === 'string'
        ? data.recommendedPlanId
        : undefined,
    defaultBillingCycleId:
      'defaultBillingCycleId' in data && typeof data.defaultBillingCycleId === 'string'
        ? data.defaultBillingCycleId
        : undefined,
    defaultPricingId:
      'defaultPricingId' in data && typeof data.defaultPricingId === 'string'
        ? data.defaultPricingId
        : undefined,
    prefill: prefillRecord
      ? {
          name: readString(prefillRecord.name),
          email: readString(prefillRecord.email),
          phone: readString(prefillRecord.phone),
          company: readString(prefillRecord.company),
          workspaceName: readString(
            (prefillRecord as Record<string, unknown>).workspace_name ??
              prefillRecord.workspaceName,
          ),
          billingAddressLine1: readString(
            (prefillRecord as Record<string, unknown>).billing_address_line_1 ??
              prefillRecord.billingAddressLine1,
          ),
          billingAddressLine2: readString(
            (prefillRecord as Record<string, unknown>).billing_address_line_2 ??
              prefillRecord.billingAddressLine2,
          ),
          billingAddressCity: readString(
            (prefillRecord as Record<string, unknown>).billing_address_city ??
              prefillRecord.billingAddressCity,
          ),
          billingAddressState: readString(
            (prefillRecord as Record<string, unknown>).billing_address_state ??
              prefillRecord.billingAddressState,
          ),
          billingAddressPostalCode: readString(
            (prefillRecord as Record<string, unknown>).billing_address_postal_code ??
              prefillRecord.billingAddressPostalCode,
          ),
          billingAddressCountry: readString(
            (prefillRecord as Record<string, unknown>).billing_address_country ??
              prefillRecord.billingAddressCountry,
          ),
          language: readString(prefillRecord.language),
        }
      : undefined,
    stripePublishableKey:
      ('stripePublishableKey' in data && typeof (data as PublicCheckoutData).stripePublishableKey === 'string'
        ? (data as PublicCheckoutData).stripePublishableKey
        : readString(dataRecord.stripe_publishable_key ?? dataRecord.stripePublishableKey)) ?? null,
    stripeAccountId:
      ('stripeAccountId' in data && typeof (data as PublicCheckoutData).stripeAccountId === 'string'
        ? (data as PublicCheckoutData).stripeAccountId
        : readString(dataRecord.stripe_account_id ?? dataRecord.stripeAccountId)) ?? null,
    stripeAccountType:
      ('stripeAccountType' in data && typeof (data as PublicCheckoutData).stripeAccountType === 'string'
        ? (data as PublicCheckoutData).stripeAccountType
        : readString(dataRecord.stripe_account_type ?? dataRecord.stripeAccountType)) ?? null,
    billingCycles:
      'billingCycles' in data && Array.isArray((data as PublicCheckoutData).billingCycles)
        ? (data as PublicCheckoutData).billingCycles
        : [],
    plans,
    creditPackages,
    checkoutFields,
    countryOptions,
  };
}

function normalizeCheckoutResponse(payload: ApiEnvelope<PublicCheckoutResponse> | undefined): PublicCheckoutResponse {
  const data = (payload?.data ?? {}) as Partial<PublicCheckoutResponse>;
  return {
    intentId: readString(data.intentId) ?? readString((data as Record<string, unknown>).intent_id),
    customerId: readString(data.customerId) ?? readString((data as Record<string, unknown>).customer_id),
    productId: readString(data.productId) ?? readString((data as Record<string, unknown>).product_id),
    planId: readString(data.planId) ?? readString((data as Record<string, unknown>).plan_id),
    packageId: readString(data.packageId) ?? readString((data as Record<string, unknown>).package_id),
    cycleId: readString(data.cycleId) ?? readString((data as Record<string, unknown>).cycle_id),
    priceId: readString(data.priceId) ?? readString((data as Record<string, unknown>).price_id),
    purchasableType:
      (readString(data.purchasableType) ??
        readString((data as Record<string, unknown>).purchasable_type)) as PublicCheckoutResponse['purchasableType'],
    currency: readString(data.currency),
    paymentRequired: Boolean(data.paymentRequired ?? (data as Record<string, unknown>).payment_required),
    clientSecret: readString(data.clientSecret) ?? readString((data as Record<string, unknown>).client_secret) ?? null,
    stripeIntentType:
      (readString(data.stripeIntentType) ??
        readString((data as Record<string, unknown>).stripe_intent_type)) as PublicCheckoutResponse['stripeIntentType'],
    stripePaymentIntentId:
      readString(data.stripePaymentIntentId) ??
      readString((data as Record<string, unknown>).stripe_payment_intent_id) ??
      null,
    stripeSubscriptionId:
      readString(data.stripeSubscriptionId) ??
      readString((data as Record<string, unknown>).stripe_subscription_id) ??
      null,
    stripeSetupIntentId:
      readString(data.stripeSetupIntentId) ??
      readString((data as Record<string, unknown>).stripe_setup_intent_id) ??
      null,
  };
}

class PublicCheckoutService {
  private buildPath(path: string) {
    const normalized = path.startsWith('/') ? path : `/${path}`;
    const prefix = API_PROXY_PREFIX || '';
    if (!prefix) return normalized;
    return `${prefix}${normalized}`.replace(`${prefix}//`, `${prefix}/`);
  }

  async getCheckout(
    productId: string,
    params?: {
      planId?: string | null;
      cycleId?: string | null;
      priceId?: string | null;
      checkoutToken?: string | null;
      lang?: string | null;
    },
  ): Promise<PublicCheckoutData> {
    try {
      const searchParams = new URLSearchParams();
      if (params?.planId) searchParams.set('planId', params.planId);
      if (params?.cycleId) searchParams.set('cycleId', params.cycleId);
      if (params?.priceId) searchParams.set('priceId', params.priceId);
      if (params?.checkoutToken) searchParams.set('checkout_token', params.checkoutToken);
      if (params?.lang) searchParams.set('lang', params.lang);
      const queryString = searchParams.toString();
      const response = await httpClient.get<ApiEnvelope<PublicCheckoutData>>(
        this.buildPath(
          `/api/v1/public/products/${productId}/checkout${queryString ? `?${queryString}` : ''}`,
        ),
      );
      return normalizeCheckoutData(response.data);
    } catch (error: unknown) {
      const status = (error as { response?: { status?: number } })?.response?.status;
      const payload = (error as { response?: { data?: unknown } })?.response?.data;
      const message = (payload as { message?: string } | undefined)?.message ?? 'Failed to load checkout data.';
      throw new PublicCheckoutRequestError(message, status, payload);
    }
  }

  async submitCheckout(body: CheckoutRequestBody): Promise<PublicCheckoutResponse> {
    try {
      const response = await httpClient.post<ApiEnvelope<PublicCheckoutResponse>>(
        this.buildPath('/api/v1/public/checkout'),
        body,
      );
      return normalizeCheckoutResponse(response.data);
    } catch (error: unknown) {
      const status = (error as { response?: { status?: number } })?.response?.status;
      const payload = (error as { response?: { data?: unknown } })?.response?.data;
      const message = (payload as { message?: string } | undefined)?.message ?? 'Checkout failed.';
      throw new PublicCheckoutRequestError(message, status, payload);
    }
  }
}

export const publicCheckoutService = new PublicCheckoutService();

const CHECKOUT_FIELD_KEYS: PublicCheckoutFieldKey[] = [
  'contact_phone',
  'company',
  'billing.address_line_1',
  'billing.address_line_2',
  'billing.address_city',
  'billing.address_state',
  'billing.address_postal_code',
  'billing.address_country',
];

const readBoolean = (value: unknown): boolean | undefined => {
  if (typeof value === 'boolean') {
    return value;
  }
  if (typeof value === 'string') {
    const normalized = value.trim().toLowerCase();
    if (normalized === 'true') {
      return true;
    }
    if (normalized === 'false') {
      return false;
    }
  }
  return undefined;
};

const readNumber = (value: unknown): number | undefined => {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value;
  }
  if (typeof value === 'string') {
    const parsed = Number.parseInt(value, 10);
    if (Number.isFinite(parsed)) {
      return parsed;
    }
  }
  return undefined;
};

const normalizeCheckoutField = (entry: unknown): PublicCheckoutFieldSetting | null => {
  if (!entry || typeof entry !== 'object') {
    return null;
  }

  const record = entry as Record<string, unknown>;
  const rawKey = readString(record.key);
  if (!rawKey || !CHECKOUT_FIELD_KEYS.includes(rawKey as PublicCheckoutFieldKey)) {
    return null;
  }

  const key = rawKey as PublicCheckoutFieldKey;
  const enabled = readBoolean(record.enabled) ?? false;
  const required = readBoolean(record.required) ?? false;
  const order = readNumber(record.order) ?? 0;
  const resolvedEnabled = required ? true : enabled;

  return {
    key,
    enabled: resolvedEnabled,
    required: resolvedEnabled ? required : false,
    order,
  };
};

function normalizeCheckoutFields(values: unknown): PublicCheckoutFieldSetting[] {
  const entries = Array.isArray(values) ? values : [];
  const normalized: PublicCheckoutFieldSetting[] = [];

  for (const entry of entries) {
    const field = normalizeCheckoutField(entry);
    if (field) {
      normalized.push(field);
    }
  }

  return normalized.sort((a, b) => {
    if (a.order !== b.order) {
      return a.order - b.order;
    }
    return a.key.localeCompare(b.key);
  });
}
